import {DocumentPriceBasis} from "@/components/docs/DocumentPriceBasis";
import { useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Pencil, Printer, Trash2, Truck } from "lucide-react";
import { PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { InvoiceDocumentView } from "@/components/invoices/InvoiceDocumentView";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useCreateDnFromInvoice,
  useDeleteInvoice,
  useInvoice,
  useRestoreInvoice,
} from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { buildInvoiceHtml, printInvoice } from "@/lib/printInvoice";
import { exportInvoiceCsv } from "@/lib/exportDocument";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";
import { DocumentExportDialog } from "@/components/domain/DocumentExportDialog";
export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { search: context } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = useCan();
  const result = useInvoice(
    id,
    new URLSearchParams(context).get("archived") === "true",
  );
  const inv = result.data;
  const createDn = useCreateDnFromInvoice();
  const remove = useDeleteInvoice();
  const restore = useRestoreInvoice();
  const lock = useRef(false);
  const [preview, setPreview] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [confirm, setConfirm] = useState<"archive" | "restore" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canEdit = Boolean(user && can("records:update"));
  const canCreate = Boolean(user && can("records:create"));
  const canDelete = Boolean(user && can("records:delete"));
  const busy = createDn.isPending || remove.isPending || restore.isPending;
  usePageCrumbs(inv ? [{ label: inv.invNo }] : result.error ? [{ label: t("common.unavailable") }] : [], `/invoices${context}`);
  if (result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (result.error || !inv)
    return (
      <PageBody>
        <ErrorState
          message={t("invoices.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  const archived = Boolean(inv.deletedAt);
  const deliveries = inv.deliveryNotes ?? [];
  const runAction = async () => {
    if (lock.current) return;
    lock.current = true;
    setError(null);
    try {
      if (confirm === "restore") {
        await restore.mutateAsync(inv.id);
        navigate(`/invoices/${inv.id}`);
      } else {
        await remove.mutateAsync(inv.id);
        navigate(`/invoices${context}`);
      }
      setConfirm(null);
    } catch (err) {
      setError(getLocalizedError(err, t, "invoices.errorSave"));
      setConfirm(null);
    } finally {
      lock.current = false;
    }
  };
  return (
    <PageBody>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-mono tabular text-xl font-semibold">
            {inv.invNo}
          </h1>
          <Link
            className="mt-1 inline-block text-sm text-brand underline"
            to={`/proforma-invoices/${inv.piId}`}
          >
            {t("common.fromPi")} {inv.piNo}
          </Link>{" "}
          {archived && <Badge>{t("invoiceWorkspace.archived")}</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPreview(true)}
          >
            <Printer className="size-4" />
            {t("invoiceWorkspace.preview")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-haspopup="dialog"
            onClick={() => setExportOpen(true)}
          >
            <Download className="size-4" />
            {t("common.export")}
          </Button>
          {!archived && canCreate && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => setDelivery(true)}
            >
              <Truck className="size-4" />
              {t("invoiceWorkspace.createDelivery")}
            </Button>
          )}
          {!archived && canEdit && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || deliveries.length > 0}
              onClick={() => navigate(`/invoices/${inv.id}/edit${context}`)}
            >
              <Pencil className="size-4" />
              {t("common.edit")}
            </Button>
          )}
          {canDelete && (
            <Button
              variant={archived ? "secondary" : "danger"}
              size="sm"
              disabled={busy || (!archived && deliveries.length > 0)}
              onClick={() => setConfirm(archived ? "restore" : "archive")}
            >
              <Trash2 className="size-4" />
              {t(
                archived
                  ? "invoiceWorkspace.restore"
                  : "invoiceWorkspace.archive",
              )}
            </Button>
          )}
        </div>
      </div>
      {deliveries.length > 0 && (
        <div className="my-3 rounded-sm border border-line bg-sunken p-3 text-xs">
          <p>{t("invoiceWorkspace.invoice_delivery_locked")}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {deliveries.map((d) => (
              <Link
                className="font-mono text-brand underline"
                to={`/delivery-notes/${d.id}`}
                key={d.id}
              >
                {d.dnNo}
              </Link>
            ))}
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-4">
      <DocumentPriceBasis snapshots={inv.priceSnapshots}/>
        <InvoiceDocumentView document={inv} />
      </div>
      <ConfirmDialog
        open={confirm !== null}
        title={t(
          confirm === "restore"
            ? "invoiceWorkspace.restore"
            : "invoiceWorkspace.archive",
        )}
        description={t(
          confirm === "restore"
            ? "invoiceWorkspace.restoreHint"
            : "invoices.confirmDelete",
          { number: inv.invNo },
        )}
        confirmLabel={t(
          confirm === "restore"
            ? "invoiceWorkspace.restore"
            : "invoiceWorkspace.archive",
        )}
        cancelLabel={t("common.cancel")}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runAction()}
      />
      {exportOpen && (
        <DocumentExportDialog
          key={inv.id}
          documentNo={inv.invNo}
          onCsv={() => exportInvoiceCsv(inv, t)}
          onPdf={() => { setExportOpen(false); setPreview(true); }}
          onExcel={async () => {
            const { exportInvoiceExcel } = await import("@/lib/exportInvoiceExcel");
            return exportInvoiceExcel(inv);
          }}
          onClose={() => setExportOpen(false)}
        />
      )}
      {preview && (
        <Modal
          className="max-w-6xl"
          title={`${t("invoiceWorkspace.preview")} · ${inv.invNo}`}
          closeLabel={t("common.close")}
          onClose={() => setPreview(false)}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 p-3">
            <SaveAsPdfHint />
            <Button onClick={() => printInvoice(inv)}>
              {t("common.print")}
            </Button>
          </div>
          <iframe
            title={t("invoiceWorkspace.preview")}
            sandbox="allow-same-origin"
            srcDoc={buildInvoiceHtml(inv)}
            className="min-h-[70dvh] w-full bg-surface"
          />
        </Modal>
      )}
      {delivery && (
        <Modal
          title={t("invoiceWorkspace.createDelivery")}
          closeLabel={t("common.close")}
          onClose={() => {
            if (!busy) setDelivery(false);
          }}
        >
          <div className="space-y-3 p-4">
            <p className="text-sm text-ink-soft">
              {t("invoiceWorkspace.deliveryHint")}
            </p>
            <p className="whitespace-pre-line text-sm">
              {inv.consigneeSnapshot || inv.shipTo || inv.billToName}
            </p>
            <TableShell>
              <thead>
                <tr>
                  <Th>{t("pi.item")}</Th>
                  <Th>{t("lot.number")}</Th>
                  <Th>{t("pi.qty")}</Th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((it) => (
                  <Tr key={it.lineNo}>
                    <Td className="font-mono text-xs">
                      {it.productCode} / {it.subRef}
                    </Td>
                    <Td className="font-mono text-xs">{it.lotNumber || "—"}</Td>
                    <Td className="font-mono text-xs">
                      {it.quantity} {it.unit}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
            <Button
              disabled={busy}
              onClick={async () => {
                if (lock.current) return;
                lock.current = true;
                setError(null);
                try {
                  setDelivery(false);
                  if(deliveries.length)navigate(`/delivery-notes/${deliveries[0]!.id}`);
                  else navigate(`/delivery-notes/new?refType=invoice&sourceId=${inv.id}`);
                } catch (err) {
                  setError(getLocalizedError(err, t, "invoices.errorSave"));
                  setDelivery(false);
                } finally {
                  lock.current = false;
                }
              }}
            >
              {t(
                deliveries.length
                  ? "invoiceWorkspace.openDelivery"
                  : "invoiceWorkspace.createDelivery",
              )}
            </Button>
          </div>
        </Modal>
      )}
    </PageBody>
  );
}
